import type {
  LidarState,
  ColorScheme,
  PointCloudInfo,
  ColormapName,
  ColorRangeConfig,
  LidarSampleDataset,
} from '../core/types';
import { FileInput } from './FileInput';
import { RangeSlider } from './RangeSlider';
import { DualRangeSlider } from './DualRangeSlider';
import { ClassificationLegend } from './ClassificationLegend';
import { Colorbar } from './Colorbar';
import { PercentileRangeControl } from './PercentileRangeControl';
import { formatNumber } from '../utils/helpers';
import { COLORMAP_NAMES, COLORMAP_LABELS } from '../colorizers/Colormaps';

/**
 * Callbacks for panel interactions
 */
export interface PanelBuilderCallbacks {
  onFileSelect: (file: File) => void;
  onUrlSubmit: (url: string) => void;
  onPointSizeChange: (size: number) => void;
  onOpacityChange: (opacity: number) => void;
  onColorSchemeChange: (scheme: ColorScheme) => void;
  onColormapChange: (colormap: ColormapName) => void;
  onColorRangeChange: (config: ColorRangeConfig) => void;
  onUsePercentileChange: (usePercentile: boolean) => void;
  onElevationRangeChange: (range: [number, number] | null) => void;
  onPickableChange: (pickable: boolean) => void;
  onZOffsetEnabledChange: (enabled: boolean) => void;
  onZOffsetChange: (offset: number) => void;
  onTerrainChange: (enabled: boolean) => void;
  onUnload: (id: string) => void;
  onZoomTo: (id: string) => void;
  onClassificationToggle: (classificationCode: number, visible: boolean) => void;
  onClassificationShowAll: () => void;
  onClassificationHideAll: () => void;
  onShowMetadata?: (id: string) => void;
  onCrossSectionPanel?: () => HTMLElement | null;
  onShareUrl?: () => string;
}

/**
 * Builds and manages the LiDAR control panel UI.
 */
export class PanelBuilder {
  private _callbacks: PanelBuilderCallbacks;
  private _state: LidarState;
  private _sampleData: LidarSampleDataset[];
  private _sampleDataLabel: string;

  // UI component references
  private _fileInput?: FileInput;
  private _urlInput?: HTMLInputElement;
  private _loadButton?: HTMLButtonElement;
  private _colorSelect?: HTMLSelectElement;
  private _colormapSelect?: HTMLSelectElement;
  private _colormapGroup?: HTMLElement;
  private _colorbar?: Colorbar;
  private _colorbarContainer?: HTMLElement;
  private _colorRangeControl?: PercentileRangeControl;
  private _colorRangeContainer?: HTMLElement;
  private _percentileCheckbox?: HTMLInputElement;
  private _percentileGroup?: HTMLElement;
  private _pointSizeSlider?: RangeSlider;
  private _opacitySlider?: RangeSlider;
  private _pointCloudsList?: HTMLElement;
  private _pickableCheckbox?: HTMLInputElement;
  private _elevationSlider?: DualRangeSlider;
  private _elevationCheckbox?: HTMLInputElement;
  private _zOffsetCheckbox?: HTMLInputElement;
  private _zOffsetSlider?: RangeSlider;
  private _zOffsetSliderContainer?: HTMLElement;
  private _terrainCheckbox?: HTMLInputElement;
  private _loadingIndicator?: HTMLElement;
  private _errorMessage?: HTMLElement;
  private _classificationLegend?: ClassificationLegend;
  private _classificationLegendContainer?: HTMLElement;
  private _shareFeedbackTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    callbacks: PanelBuilderCallbacks,
    initialState: LidarState,
    options?: { sampleData?: LidarSampleDataset[]; sampleDataLabel?: string },
  ) {
    this._callbacks = callbacks;
    this._state = initialState;
    this._sampleData = options?.sampleData ?? [];
    this._sampleDataLabel = options?.sampleDataLabel ?? 'Load sample data...';
  }

  /**
   * Builds and returns the panel content.
   *
   * @returns The panel content element
   */
  build(): HTMLElement {
    const content = document.createElement('div');
    content.className = 'lidar-control-content';
    // The content fills the panel (flex: 1) and scrolls as needed; the panel's
    // own height/max-height is managed by LidarControl._updatePanelPosition().

    // File input section
    content.appendChild(this._buildFileSection());

    // Styling section
    content.appendChild(this._buildStylingSection());

    // Point clouds list
    content.appendChild(this._buildPointCloudsList());

    // Cross-section panel (if callback provided)
    const crossSectionPanel = this._buildCrossSectionSection();
    if (crossSectionPanel) {
      content.appendChild(crossSectionPanel);
    }

    // Loading indicator
    content.appendChild(this._buildLoadingIndicator());

    // Error message
    content.appendChild(this._buildErrorMessage());

    const shareSection = this._buildShareSection();
    if (shareSection) {
      content.appendChild(shareSection);
    }

    return content;
  }

  /**
   * Updates the UI to reflect the current state.
   *
   * @param state - New state
   */
  updateState(state: LidarState): void {
    this._state = state;

    // Update loading state
    if (this._loadingIndicator) {
      if (state.loading) {
        this._loadingIndicator.classList.add('active');
      } else {
        this._loadingIndicator.classList.remove('active');
      }
    }

    // Update error message
    if (this._errorMessage) {
      if (state.error) {
        this._errorMessage.textContent = state.error;
        this._errorMessage.style.display = 'block';
      } else {
        this._errorMessage.style.display = 'none';
      }
    }

    // Update point clouds list
    this._updatePointCloudsList();

    // Update sliders
    if (this._pointSizeSlider) {
      this._pointSizeSlider.setValue(state.pointSize);
    }
    if (this._opacitySlider) {
      this._opacitySlider.setValue(state.opacity);
    }

    // Update color scheme
    if (this._colorSelect && typeof state.colorScheme === 'string') {
      this._colorSelect.value = state.colorScheme;
      this._updatePercentileVisibility(state.colorScheme);
    }

    // Update colormap selector
    if (this._colormapSelect && state.colormap) {
      this._colormapSelect.value = state.colormap;
    }

    // Update colorbar
    if (this._colorbar) {
      if (state.colormap) {
        this._colorbar.setColormap(state.colormap);
      }
      if (state.computedColorBounds) {
        this._colorbar.setRange(state.computedColorBounds.min, state.computedColorBounds.max);
      }
    }

    // Update color range control
    if (this._colorRangeControl && state.colorRange) {
      this._colorRangeControl.setConfig(state.colorRange);
      // Also update data bounds based on current color scheme
      const bounds = this._getDataBoundsForCurrentScheme();
      this._colorRangeControl.setDataBounds(bounds);
      // Update computed bounds for mode switching
      if (state.computedColorBounds) {
        this._colorRangeControl.setComputedBounds(state.computedColorBounds);
      }
    }

    // Update percentile checkbox (legacy)
    if (this._percentileCheckbox) {
      this._percentileCheckbox.checked = state.usePercentile ?? true;
    }

    // Update pickable checkbox
    if (this._pickableCheckbox) {
      this._pickableCheckbox.checked = state.pickable ?? false;
    }

    // Update Z offset controls
    if (this._zOffsetCheckbox) {
      this._zOffsetCheckbox.checked = state.zOffsetEnabled ?? false;
    }
    if (this._zOffsetSliderContainer) {
      this._zOffsetSliderContainer.style.display = state.zOffsetEnabled ? 'block' : 'none';
    }
    if (this._zOffsetSlider) {
      // Update slider bounds centered around -zOffsetBase (the default z-offset)
      if (state.zOffsetBase !== undefined) {
        const defaultOffset = -state.zOffsetBase;
        const sliderMin = defaultOffset - 100;
        const sliderMax = defaultOffset + 100;
        this._zOffsetSlider.setBounds(sliderMin, sliderMax);
      }
      this._zOffsetSlider.setValue(state.zOffset ?? 0);
    }

    // Update terrain checkbox
    if (this._terrainCheckbox) {
      this._terrainCheckbox.checked = state.terrainEnabled ?? false;
    }

    // Update elevation slider bounds when point clouds change
    if (this._elevationSlider && state.pointClouds.length > 0) {
      const bounds = this._getElevationBounds();
      this._elevationSlider.setBounds(bounds.min, bounds.max);
      // If filter is not active, reset range to full bounds
      if (!this._elevationCheckbox?.checked) {
        this._elevationSlider.setRange(bounds.min, bounds.max);
      }
    }

    // Update classification legend
    if (this._classificationLegend && state.availableClassifications) {
      this._classificationLegend.setClassifications(
        Array.from(state.availableClassifications),
        state.hiddenClassifications || new Set()
      );
    }

    // Disable/enable inputs during loading
    const enabled = !state.loading;
    this._fileInput?.setEnabled(enabled);
    if (this._urlInput) this._urlInput.disabled = !enabled;
    if (this._loadButton) this._loadButton.disabled = !enabled;
  }

  /**
   * Updates the loading progress display.
   *
   * @param progress - Progress value (0-100)
   * @param message - Optional progress message
   */
  updateLoadingProgress(progress: number, message?: string): void {
    if (!this._loadingIndicator) return;

    const progressBar = this._loadingIndicator.querySelector('.lidar-loading-bar-fill') as HTMLElement;
    const progressText = this._loadingIndicator.querySelector('.lidar-loading-progress') as HTMLElement;

    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    if (progressText && message) {
      progressText.textContent = message;
    }
  }

  /**
   * Builds the file input section.
   */
  private _buildFileSection(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'lidar-control-section';

    // File upload
    this._fileInput = new FileInput({
      accept: '.las,.laz',
      onChange: (file) => this._callbacks.onFileSelect(file),
    });
    section.appendChild(this._fileInput.render());

    // Sample-data dropdown (opt-in; fills the URL input)
    const sampleDropdown = this._buildSampleDropdown();
    if (sampleDropdown) section.appendChild(sampleDropdown);

    // URL input
    const urlGroup = document.createElement('div');
    urlGroup.className = 'lidar-control-group';
    urlGroup.style.marginTop = '12px';

    const urlLabel = document.createElement('label');
    urlLabel.className = 'lidar-control-label';
    urlLabel.textContent = 'Load COPC or EPT from URL';
    urlGroup.appendChild(urlLabel);

    const urlRow = document.createElement('div');
    urlRow.className = 'lidar-control-flex';

    const urlInput = document.createElement('input');
    urlInput.type = 'text';
    urlInput.className = 'lidar-control-input';
    urlInput.placeholder = 'https://example.com/pointcloud.laz';
    urlInput.style.flex = '1';
    this._urlInput = urlInput;

    const loadBtn = document.createElement('button');
    loadBtn.type = 'button';
    loadBtn.className = 'lidar-control-button';
    loadBtn.textContent = 'Load';
    loadBtn.addEventListener('click', () => {
      const url = urlInput.value.trim();
      if (url) {
        this._callbacks.onUrlSubmit(url);
        urlInput.value = '';
      }
    });
    this._loadButton = loadBtn;

    // Allow Enter key to submit
    urlInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        loadBtn.click();
      }
    });

    urlRow.appendChild(urlInput);
    urlRow.appendChild(loadBtn);
    urlGroup.appendChild(urlRow);
    section.appendChild(urlGroup);

    return section;
  }

  /**
   * Builds the "Load sample data" dropdown: a custom (not native `<select>`)
   * dropdown so the menu themes correctly in dark mode. Picking an entry fills
   * the URL input. Returns null when no samples are configured.
   */
  private _buildSampleDropdown(): HTMLElement | null {
    if (this._sampleData.length === 0) return null;

    const group = document.createElement('div');
    group.className = 'lidar-control-group';
    group.style.marginTop = '12px';

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.textContent = 'Sample data';
    group.appendChild(label);

    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'lidar-sample-trigger-label';
    triggerLabel.textContent = this._sampleDataLabel;
    const caret = document.createElement('span');
    caret.className = 'lidar-sample-caret';
    caret.textContent = '▾';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'lidar-sample-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', this._sampleDataLabel);
    trigger.appendChild(triggerLabel);
    trigger.appendChild(caret);

    const menu = document.createElement('div');
    menu.className = 'lidar-sample-menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    let menuOpen = false;
    const setMenuOpen = (open: boolean): void => {
      menuOpen = open;
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      trigger.classList.toggle('open', open);
      if (open) (menu.firstElementChild as HTMLElement | null)?.focus();
    };

    for (const sample of this._sampleData) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'lidar-sample-option';
      option.setAttribute('role', 'option');
      option.textContent = sample.label;
      option.title = sample.url;
      option.addEventListener('click', () => {
        setMenuOpen(false);
        trigger.focus();
        if (this._urlInput) this._urlInput.value = sample.url;
      });
      menu.appendChild(option);
    }

    trigger.addEventListener('click', () => setMenuOpen(!menuOpen));

    const dropdown = document.createElement('div');
    dropdown.className = 'lidar-sample-dropdown';
    dropdown.appendChild(trigger);
    dropdown.appendChild(menu);
    group.appendChild(dropdown);

    // Close on Escape or when focus leaves the dropdown (no document listener).
    group.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
        trigger.focus();
      }
    });
    group.addEventListener('focusout', (e) => {
      const next = e.relatedTarget as Node | null;
      if (!next || !group.contains(next)) setMenuOpen(false);
    });

    return group;
  }

  /**
   * Builds the styling controls section.
   */
  private _buildStylingSection(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'lidar-control-section';

    // Section header
    const header = document.createElement('div');
    header.className = 'lidar-control-section-header';
    header.textContent = 'Styling';
    section.appendChild(header);

    // Color scheme selector
    const colorGroup = document.createElement('div');
    colorGroup.className = 'lidar-control-group';

    const colorLabel = document.createElement('label');
    colorLabel.className = 'lidar-control-label';
    colorLabel.textContent = 'Color By';
    colorGroup.appendChild(colorLabel);

    const colorSelect = document.createElement('select');
    colorSelect.className = 'lidar-control-select';
    colorSelect.innerHTML = `
      <option value="elevation">Elevation</option>
      <option value="intensity">Intensity</option>
      <option value="classification">Classification</option>
      <option value="rgb">RGB (if available)</option>
    `;
    colorSelect.value = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    colorSelect.addEventListener('change', () => {
      this._callbacks.onColorSchemeChange(colorSelect.value as ColorScheme);
      // Show/hide percentile option based on color scheme
      this._updatePercentileVisibility(colorSelect.value);
    });
    this._colorSelect = colorSelect;
    colorGroup.appendChild(colorSelect);
    section.appendChild(colorGroup);

    // Colormap selector (shown only for elevation and intensity)
    section.appendChild(this._buildColormapSelector());

    // Colorbar (shown only for elevation and intensity)
    section.appendChild(this._buildColorbar());

    // Classification legend (shown only when classification scheme is selected)
    section.appendChild(this._buildClassificationLegend());

    // Color range control (replaces percentile checkbox)
    section.appendChild(this._buildColorRangeControl());

    // Point size slider
    this._pointSizeSlider = new RangeSlider({
      label: 'Point Size',
      min: 1,
      max: 10,
      step: 0.5,
      value: this._state.pointSize,
      onChange: (v) => this._callbacks.onPointSizeChange(v),
    });
    section.appendChild(this._pointSizeSlider.render());

    // Opacity slider
    this._opacitySlider = new RangeSlider({
      label: 'Opacity',
      min: 0,
      max: 1,
      step: 0.05,
      value: this._state.opacity,
      onChange: (v) => this._callbacks.onOpacityChange(v),
    });
    section.appendChild(this._opacitySlider.render());

    // 3D Terrain toggle
    section.appendChild(this._buildTerrainCheckbox());

    // Pickable checkbox
    section.appendChild(this._buildPickableCheckbox());

    // Elevation filter (collapsible)
    section.appendChild(this._buildElevationFilter());

    // Z offset control (collapsible)
    section.appendChild(this._buildZOffsetControl());

    return section;
  }

  /**
   * Builds the elevation filter controls with checkbox and dual slider.
   */
  private _buildElevationFilter(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'lidar-control-group';

    // Checkbox row
    const labelRow = document.createElement('div');
    labelRow.className = 'lidar-control-label-row';
    labelRow.style.cursor = 'pointer';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = 'lidar-elevation-filter-checkbox';
    checkbox.style.marginRight = '6px';
    this._elevationCheckbox = checkbox;

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.htmlFor = 'lidar-elevation-filter-checkbox';
    label.style.display = 'inline';
    label.style.cursor = 'pointer';
    label.textContent = 'Elevation Filter';

    labelRow.appendChild(checkbox);
    labelRow.appendChild(label);
    group.appendChild(labelRow);

    // Slider container (hidden by default)
    const sliderContainer = document.createElement('div');
    sliderContainer.style.display = 'none';
    sliderContainer.style.marginTop = '8px';

    // Get elevation bounds from loaded point clouds
    const bounds = this._getElevationBounds();

    // Create dual range slider
    this._elevationSlider = new DualRangeSlider({
      label: 'Range (m)',
      min: bounds.min,
      max: bounds.max,
      step: 1,
      valueLow: bounds.min,
      valueHigh: bounds.max,
      onChange: (low, high) => {
        if (checkbox.checked) {
          this._callbacks.onElevationRangeChange([low, high]);
        }
      },
      formatValue: (v) => v.toFixed(0),
    });

    sliderContainer.appendChild(this._elevationSlider.render());
    group.appendChild(sliderContainer);

    // Toggle visibility and filter
    checkbox.addEventListener('change', () => {
      sliderContainer.style.display = checkbox.checked ? 'block' : 'none';
      if (checkbox.checked) {
        // Update bounds when enabling filter
        const newBounds = this._getElevationBounds();
        this._elevationSlider?.setBounds(newBounds.min, newBounds.max);
        this._elevationSlider?.setRange(newBounds.min, newBounds.max);
        // Apply current range
        const range = this._elevationSlider?.getRange();
        if (range) {
          this._callbacks.onElevationRangeChange(range);
        }
      } else {
        this._callbacks.onElevationRangeChange(null);
      }
    });

    return group;
  }

  /**
   * Builds the Z offset control with checkbox and slider.
   */
  private _buildZOffsetControl(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'lidar-control-group';

    // Checkbox row
    const labelRow = document.createElement('div');
    labelRow.className = 'lidar-control-label-row';
    labelRow.style.cursor = 'pointer';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = 'lidar-zoffset-checkbox';
    checkbox.checked = this._state.zOffsetEnabled ?? false;
    checkbox.style.marginRight = '6px';
    this._zOffsetCheckbox = checkbox;

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.htmlFor = 'lidar-zoffset-checkbox';
    label.style.display = 'inline';
    label.style.cursor = 'pointer';
    label.textContent = 'Z Offset';

    labelRow.appendChild(checkbox);
    labelRow.appendChild(label);
    group.appendChild(labelRow);

    // Slider container (hidden by default)
    const sliderContainer = document.createElement('div');
    sliderContainer.style.display = this._state.zOffsetEnabled ? 'block' : 'none';
    sliderContainer.style.marginTop = '8px';
    this._zOffsetSliderContainer = sliderContainer;

    // Slider range centered around -zOffsetBase (the default z-offset for relative height)
    // Range: [-zOffsetBase - 100, -zOffsetBase + 100]
    const zOffsetBase = this._state.zOffsetBase ?? 0;
    const defaultOffset = -zOffsetBase;
    const sliderMin = defaultOffset - 100;
    const sliderMax = defaultOffset + 100;

    // Create slider
    this._zOffsetSlider = new RangeSlider({
      label: 'Offset (m)',
      min: sliderMin,
      max: sliderMax,
      step: 1,
      value: this._state.zOffset ?? defaultOffset,
      onChange: (v) => this._callbacks.onZOffsetChange(v),
    });

    sliderContainer.appendChild(this._zOffsetSlider.render());
    group.appendChild(sliderContainer);

    // Toggle visibility and enable/disable offset
    checkbox.addEventListener('change', () => {
      sliderContainer.style.display = checkbox.checked ? 'block' : 'none';
      this._callbacks.onZOffsetEnabledChange(checkbox.checked);
      if (!checkbox.checked) {
        // Reset offset to 0 when disabled
        this._zOffsetSlider?.setValue(0);
        this._callbacks.onZOffsetChange(0);
      }
    });

    return group;
  }

  /**
   * Builds the 3D terrain toggle checkbox.
   */
  private _buildTerrainCheckbox(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'lidar-control-group';

    const labelRow = document.createElement('div');
    labelRow.className = 'lidar-control-label-row';
    labelRow.style.cursor = 'pointer';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = 'lidar-terrain-checkbox';
    checkbox.checked = this._state.terrainEnabled ?? false;
    checkbox.style.marginRight = '6px';
    this._terrainCheckbox = checkbox;

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.htmlFor = 'lidar-terrain-checkbox';
    label.style.display = 'inline';
    label.style.cursor = 'pointer';
    label.textContent = '3D Terrain';

    labelRow.appendChild(checkbox);
    labelRow.appendChild(label);
    group.appendChild(labelRow);

    checkbox.addEventListener('change', () => {
      this._callbacks.onTerrainChange(checkbox.checked);
    });

    return group;
  }

  /**
   * Gets the elevation bounds from loaded point clouds.
   */
  private _getElevationBounds(): { min: number; max: number } {
    if (this._state.pointClouds.length === 0) {
      return { min: 0, max: 100 };
    }

    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const pc of this._state.pointClouds) {
      minZ = Math.min(minZ, pc.bounds.minZ);
      maxZ = Math.max(maxZ, pc.bounds.maxZ);
    }

    // Round to nice values
    minZ = Math.floor(minZ);
    maxZ = Math.ceil(maxZ);

    return { min: minZ, max: maxZ };
  }

  /**
   * Gets the intensity bounds.
   * Intensity values are normalized to 0-1 range during loading.
   */
  private _getIntensityBounds(): { min: number; max: number } {
    return { min: 0, max: 1 };
  }

  /**
   * Gets the appropriate data bounds based on the current color scheme.
   */
  private _getDataBoundsForCurrentScheme(): { min: number; max: number } {
    const colorScheme = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    if (colorScheme === 'intensity') {
      return this._getIntensityBounds();
    }
    return this._getElevationBounds();
  }

  /**
   * Builds the colormap selector dropdown.
   */
  private _buildColormapSelector(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'lidar-colormap-group';
    this._colormapGroup = group;

    // Set initial visibility based on current color scheme
    const currentScheme = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    const showColormap = currentScheme === 'elevation' || currentScheme === 'intensity';
    group.style.display = showColormap ? 'block' : 'none';

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.textContent = 'Colormap';
    group.appendChild(label);

    // Select dropdown
    const select = document.createElement('select');
    select.className = 'lidar-colormap-select';

    for (const name of COLORMAP_NAMES) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = COLORMAP_LABELS[name];
      select.appendChild(option);
    }
    select.value = this._state.colormap || 'viridis';
    this._colormapSelect = select;

    select.addEventListener('change', () => {
      const colormap = select.value as ColormapName;
      this._callbacks.onColormapChange(colormap);
    });

    group.appendChild(select);

    return group;
  }

  /**
   * Builds the colorbar component.
   */
  private _buildColorbar(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'lidar-control-group';
    this._colorbarContainer = container;

    // Set initial visibility based on current color scheme
    const currentScheme = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    const showColorbar = (currentScheme === 'elevation' || currentScheme === 'intensity') && this._state.showColorbar;
    container.style.display = showColorbar ? 'block' : 'none';

    // Create the colorbar
    this._colorbar = new Colorbar({
      colormap: this._state.colormap || 'viridis',
      minValue: this._state.computedColorBounds?.min ?? 0,
      maxValue: this._state.computedColorBounds?.max ?? 100,
    });

    container.appendChild(this._colorbar.render());
    return container;
  }

  /**
   * Builds the color range control (replaces percentile checkbox).
   */
  private _buildColorRangeControl(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'lidar-control-group';
    this._colorRangeContainer = container;

    // Set initial visibility based on current color scheme
    const currentScheme = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    const showControl = currentScheme === 'elevation' || currentScheme === 'intensity';
    container.style.display = showControl ? 'block' : 'none';

    // Get data bounds based on current color scheme
    const dataBounds = this._getDataBoundsForCurrentScheme();

    // Create the control
    this._colorRangeControl = new PercentileRangeControl({
      config: this._state.colorRange || {
        mode: 'percentile',
        percentileLow: 2,
        percentileHigh: 98,
      },
      dataBounds,
      computedBounds: this._state.computedColorBounds,
      onChange: (config) => {
        this._callbacks.onColorRangeChange(config);
      },
    });

    container.appendChild(this._colorRangeControl.render());
    return container;
  }

  /**
   * Updates the visibility of color-related controls based on color scheme.
   * Shows colormap/colorbar/range for elevation and intensity.
   * Shows classification legend for classification.
   */
  private _updatePercentileVisibility(colorScheme: string): void {
    const showColorControls = colorScheme === 'elevation' || colorScheme === 'intensity';

    // Show/hide colormap selector
    if (this._colormapGroup) {
      this._colormapGroup.style.display = showColorControls ? 'block' : 'none';
    }

    // Show/hide colorbar
    if (this._colorbarContainer) {
      this._colorbarContainer.style.display = showColorControls && this._state.showColorbar ? 'block' : 'none';
    }

    // Show/hide color range control and update bounds for the new scheme
    if (this._colorRangeContainer) {
      this._colorRangeContainer.style.display = showColorControls ? 'block' : 'none';
    }
    if (this._colorRangeControl && showColorControls) {
      // Update data bounds when switching between elevation and intensity
      const bounds = this._getDataBoundsForCurrentScheme();
      this._colorRangeControl.setDataBounds(bounds);
    }

    // Legacy percentile group (keep for backward compatibility)
    if (this._percentileGroup) {
      this._percentileGroup.style.display = 'none'; // Hide legacy control
    }

    // Show/hide classification legend
    if (this._classificationLegendContainer) {
      this._classificationLegendContainer.style.display =
        colorScheme === 'classification' ? 'block' : 'none';
    }
  }

  /**
   * Builds the classification legend component.
   */
  private _buildClassificationLegend(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'lidar-control-group';
    this._classificationLegendContainer = container;

    // Set initial visibility based on current color scheme
    const currentScheme = typeof this._state.colorScheme === 'string' ? this._state.colorScheme : 'elevation';
    container.style.display = currentScheme === 'classification' ? 'block' : 'none';

    // Create the legend component
    this._classificationLegend = new ClassificationLegend({
      classifications: Array.from(this._state.availableClassifications || new Set()),
      hiddenClassifications: this._state.hiddenClassifications || new Set(),
      onToggle: (code, visible) => this._callbacks.onClassificationToggle(code, visible),
      onShowAll: () => this._callbacks.onClassificationShowAll(),
      onHideAll: () => this._callbacks.onClassificationHideAll(),
    });

    container.appendChild(this._classificationLegend.render());
    return container;
  }

  /**
   * Builds the pickable checkbox control.
   */
  private _buildPickableCheckbox(): HTMLElement {
    const group = document.createElement('div');
    group.className = 'lidar-control-group';

    const labelRow = document.createElement('div');
    labelRow.className = 'lidar-control-label-row';
    labelRow.style.cursor = 'pointer';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = 'lidar-pickable-checkbox';
    checkbox.checked = this._state.pickable ?? false;
    checkbox.style.marginRight = '6px';
    this._pickableCheckbox = checkbox;

    const label = document.createElement('label');
    label.className = 'lidar-control-label';
    label.htmlFor = 'lidar-pickable-checkbox';
    label.style.display = 'inline';
    label.style.cursor = 'pointer';
    label.textContent = 'Enable point picking';

    checkbox.addEventListener('change', () => {
      this._callbacks.onPickableChange(checkbox.checked);
    });

    labelRow.appendChild(checkbox);
    labelRow.appendChild(label);
    group.appendChild(labelRow);

    return group;
  }

  /**
   * Builds the loaded point clouds list.
   */
  private _buildPointCloudsList(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'lidar-control-section lidar-pointclouds-section';

    const header = document.createElement('div');
    header.className = 'lidar-control-section-header';
    header.textContent = 'Loaded Point Clouds';
    section.appendChild(header);

    const list = document.createElement('div');
    list.className = 'lidar-pointclouds-list';
    this._pointCloudsList = list;
    section.appendChild(list);

    this._updatePointCloudsList();

    return section;
  }

  /**
   * Updates the point clouds list display.
   */
  private _updatePointCloudsList(): void {
    if (!this._pointCloudsList) return;

    this._pointCloudsList.innerHTML = '';

    if (this._state.pointClouds.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'lidar-pointclouds-empty';
      empty.textContent = 'No point clouds loaded';
      this._pointCloudsList.appendChild(empty);
      return;
    }

    for (const pc of this._state.pointClouds) {
      this._pointCloudsList.appendChild(this._buildPointCloudItem(pc));
    }
  }

  /**
   * Builds a single point cloud list item.
   */
  private _buildPointCloudItem(pc: PointCloudInfo): HTMLElement {
    const item = document.createElement('div');
    item.className = 'lidar-pointcloud-item';

    const info = document.createElement('div');
    info.className = 'lidar-pointcloud-info';

    const name = document.createElement('div');
    name.className = 'lidar-pointcloud-name';
    name.textContent = pc.name;
    name.title = pc.name;

    const details = document.createElement('div');
    details.className = 'lidar-pointcloud-details';
    details.textContent = `${formatNumber(pc.pointCount)} points`;

    info.appendChild(name);
    info.appendChild(details);

    const actions = document.createElement('div');
    actions.className = 'lidar-pointcloud-actions';

    // Info button
    if (this._callbacks.onShowMetadata) {
      const infoBtn = document.createElement('button');
      infoBtn.type = 'button';
      infoBtn.className = 'lidar-pointcloud-action info';
      infoBtn.textContent = 'Info';
      infoBtn.title = 'Show metadata';
      infoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._callbacks.onShowMetadata!(pc.id);
      });
      actions.appendChild(infoBtn);
    }

    const zoomBtn = document.createElement('button');
    zoomBtn.type = 'button';
    zoomBtn.className = 'lidar-pointcloud-action';
    zoomBtn.textContent = 'Zoom';
    zoomBtn.title = 'Zoom to point cloud';
    zoomBtn.addEventListener('click', () => this._callbacks.onZoomTo(pc.id));

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'lidar-pointcloud-action remove';
    removeBtn.textContent = 'Remove';
    removeBtn.title = 'Remove point cloud';
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent click-outside handler from collapsing panel
      this._callbacks.onUnload(pc.id);
    });

    actions.appendChild(zoomBtn);
    actions.appendChild(removeBtn);

    item.appendChild(info);
    item.appendChild(actions);

    return item;
  }

  /**
   * Builds the loading indicator.
   */
  private _buildLoadingIndicator(): HTMLElement {
    const loading = document.createElement('div');
    loading.className = 'lidar-loading';
    loading.innerHTML = `
      <div class="lidar-loading-spinner"></div>
      <div class="lidar-loading-text">Loading point cloud...</div>
      <div class="lidar-loading-progress">Preparing...</div>
      <div class="lidar-loading-bar">
        <div class="lidar-loading-bar-fill"></div>
      </div>
    `;
    this._loadingIndicator = loading;
    return loading;
  }

  /**
   * Builds the error message display.
   */
  private _buildErrorMessage(): HTMLElement {
    const error = document.createElement('div');
    error.className = 'lidar-error';
    error.style.display = 'none';
    this._errorMessage = error;
    return error;
  }

  /**
   * Builds the share URL button section if sharing is enabled.
   */
  private _buildShareSection(): HTMLElement | null {
    if (!this._callbacks.onShareUrl) return null;

    const section = document.createElement('div');
    section.className = 'lidar-control-section lidar-share-section';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lidar-control-button lidar-share-button';
    button.textContent = 'Share URL';

    const status = document.createElement('div');
    status.className = 'lidar-share-status';
    status.setAttribute('aria-live', 'polite');

    button.addEventListener('click', async () => {
      try {
        const url = this._callbacks.onShareUrl?.();
        if (!url) {
          throw new Error('No share URL available');
        }
        await this._copyToClipboard(url);
        this._setShareStatus(status, 'Copied');
      } catch {
        this._setShareStatus(status, 'Copy failed');
      }
    });

    section.appendChild(button);
    section.appendChild(status);

    return section;
  }

  /**
   * Copies text to the clipboard, falling back to a hidden textarea for older browsers.
   */
  private async _copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    textarea.remove();

    if (!copied) {
      throw new Error('Clipboard copy failed');
    }
  }

  /**
   * Shows short share button feedback.
   */
  private _setShareStatus(status: HTMLElement, message: string): void {
    status.textContent = message;
    if (this._shareFeedbackTimeout) {
      clearTimeout(this._shareFeedbackTimeout);
    }
    this._shareFeedbackTimeout = setTimeout(() => {
      status.textContent = '';
    }, 2000);
  }

  /**
   * Builds the cross-section section if callback is provided.
   *
   * @returns Cross-section section or null if not available
   */
  private _buildCrossSectionSection(): HTMLElement | null {
    if (!this._callbacks.onCrossSectionPanel) return null;

    const panel = this._callbacks.onCrossSectionPanel();
    if (!panel) return null;

    const section = document.createElement('div');
    section.className = 'lidar-control-section lidar-crosssection-section';

    const header = document.createElement('div');
    header.className = 'lidar-control-section-header lidar-section-collapsible';
    header.innerHTML = '<span class="lidar-section-toggle">▶</span> Cross-Section';
    header.style.cursor = 'pointer';

    const body = document.createElement('div');
    body.className = 'lidar-section-body';
    body.style.display = 'none';
    body.appendChild(panel);

    header.addEventListener('click', () => {
      const toggle = header.querySelector('.lidar-section-toggle');
      if (body.style.display === 'none') {
        body.style.display = 'block';
        if (toggle) toggle.textContent = '▼';
      } else {
        body.style.display = 'none';
        if (toggle) toggle.textContent = '▶';
      }
    });

    section.appendChild(header);
    section.appendChild(body);

    return section;
  }
}
